import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './PendingFulfillmentVertical.css';

function PendingFulfillmentVertical() {
  const [activeTab, setActiveTab] = useState('orders'); // 'orders' or 'requirements'
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [sortColumn, setSortColumn] = useState('shippableLineItems');
  const [sortDirection, setSortDirection] = useState('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [filterShippable, setFilterShippable] = useState('all'); // 'all', 'fully', 'partial', 'none'
  const [expandedOrders, setExpandedOrders] = useState({}); // Track expanded orders
  const [orderLineItems, setOrderLineItems] = useState({}); // Cache line items
  const [loadingLineItems, setLoadingLineItems] = useState({}); // Track loading state
  const [itemRequirements, setItemRequirements] = useState([]); // Item requirements data
  const [loadingRequirements, setLoadingRequirements] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [requirementsSortColumn, setRequirementsSortColumn] = useState('total_needed');
  const [requirementsSortDirection, setRequirementsSortDirection] = useState('desc');
  const ordersPerPage = 100;

  // Fetch pending fulfillment orders (with optional force refresh)
  const fetchOrders = async (forceRefresh = false, silent = false) => {
    if (!silent) {
      setLoading(true);
    }
    try {
      const url = forceRefresh
        ? '/api/pending-fulfillment?refresh=true'
        : '/api/pending-fulfillment';
      const response = await axios.get(url);
      setOrders(response.data);
      setLastUpdate(new Date());
      setError(null);
    } catch (err) {
      console.error('Failed to fetch pending fulfillment orders:', err);
      setError('Failed to load pending fulfillment orders');
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  };

  // Fetch item requirements
  const fetchItemRequirements = async () => {
    setLoadingRequirements(true);
    try {
      const response = await axios.get('/api/pending-fulfillment/item-requirements');
      setItemRequirements(response.data);
    } catch (err) {
      console.error('Failed to fetch item requirements:', err);
      setError('Failed to load item requirements');
    } finally {
      setLoadingRequirements(false);
    }
  };

  // Initial fetch
  useEffect(() => {
    fetchOrders();
    // Auto-refresh every 5 minutes (silent to prevent jolt)
    const interval = setInterval(() => {
      fetchOrders(false, true); // silent refresh
    }, 300000);
    return () => clearInterval(interval);
  }, []);

  // Fetch item requirements when switching to requirements tab
  useEffect(() => {
    if (activeTab === 'requirements' && itemRequirements.length === 0) {
      fetchItemRequirements();
    }
  }, [activeTab]);

  // Manual refresh
  const handleManualRefresh = async () => {
    setRefreshing(true);
    await fetchOrders(true, false);
    if (activeTab === 'requirements') {
      await fetchItemRequirements();
    }
    setRefreshing(false);
  };

  // Handle column sort
  const handleColumnSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  // Filter orders based on shippability
  const filteredOrders = orders.filter(order => {
    if (filterShippable === 'all') return true;

    const isFullyShippable = order.shippableLineItems === order.totalLineItems && order.totalLineItems > 0;
    const isPartiallyShippable = order.shippableLineItems > 0 && order.shippableLineItems < order.totalLineItems;
    const isNotShippable = order.shippableLineItems === 0 && order.totalLineItems > 0;

    if (filterShippable === 'fully') return isFullyShippable;
    if (filterShippable === 'partial') return isPartiallyShippable;
    if (filterShippable === 'none') return isNotShippable;

    return true;
  });

  // Sort filtered orders
  const sortedOrders = [...filteredOrders].sort((a, b) => {
    let aVal = a[sortColumn];
    let bVal = b[sortColumn];

    // Handle dates
    if (sortColumn === 'tranDate' || sortColumn === 'shipDate') {
      aVal = new Date(aVal || 0);
      bVal = new Date(bVal || 0);
    }

    // Handle numbers
    if (sortColumn === 'total' || sortColumn === 'age' || sortColumn === 'shippableLineItems') {
      aVal = parseFloat(aVal || 0);
      bVal = parseFloat(bVal || 0);
    }

    // Handle strings
    if (typeof aVal === 'string') {
      return sortDirection === 'asc'
        ? aVal.localeCompare(bVal)
        : bVal.localeCompare(aVal);
    }

    // Handle numbers and dates
    return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
  });

  // Pagination calculations
  const totalPages = Math.ceil(sortedOrders.length / ordersPerPage);
  const indexOfLastOrder = currentPage * ordersPerPage;
  const indexOfFirstOrder = indexOfLastOrder - ordersPerPage;
  const currentOrders = sortedOrders.slice(indexOfFirstOrder, indexOfLastOrder);

  // Calculate totals
  const totalShippableOrders = sortedOrders.filter(order =>
    order.shippableLineItems > 0
  ).length;

  const fullyShippableOrders = sortedOrders.filter(order =>
    order.shippableLineItems === order.totalLineItems && order.totalLineItems > 0
  ).length;

  // Pagination controls
  const goToPage = (pageNumber) => {
    setCurrentPage(Math.min(Math.max(1, pageNumber), totalPages));
  };

  // Open order in NetSuite
  const openInNetSuite = (e, orderId) => {
    e.stopPropagation();
    const netsuiteUrl = `https://3646798.app.netsuite.com/app/accounting/transactions/salesord.nl?id=${orderId}`;
    window.open(netsuiteUrl, '_blank');
  };

  // Toggle order expansion and fetch line items if needed
  const toggleOrderExpansion = async (orderId) => {
    const isExpanded = expandedOrders[orderId];

    if (!isExpanded && !orderLineItems[orderId]) {
      // Fetch line items if not already cached
      setLoadingLineItems({ ...loadingLineItems, [orderId]: true });
      try {
        const response = await axios.get(`/api/pending-fulfillment/${orderId}/line-items`);
        setOrderLineItems({ ...orderLineItems, [orderId]: response.data });
      } catch (err) {
        console.error(`Failed to fetch line items for order ${orderId}:`, err);
      } finally {
        setLoadingLineItems({ ...loadingLineItems, [orderId]: false });
      }
    }

    setExpandedOrders({ ...expandedOrders, [orderId]: !isExpanded });
  };

  // Format date as MM/DD/YY
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const year = String(date.getFullYear()).slice(-2);
    return `${month}/${day}/${year}`;
  };

  // Format currency
  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return 'N/A';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  if (loading && orders.length === 0) {
    return (
      <div className="loading-container-vertical">
        <div className="loader-vertical">Loading pending fulfillment orders...</div>
      </div>
    );
  }

  // Sort requirements
  const sortedRequirements = [...itemRequirements].sort((a, b) => {
    let aVal = a[requirementsSortColumn];
    let bVal = b[requirementsSortColumn];

    // Handle strings
    if (typeof aVal === 'string') {
      return requirementsSortDirection === 'asc'
        ? aVal.localeCompare(bVal)
        : bVal.localeCompare(aVal);
    }

    // Handle numbers
    return requirementsSortDirection === 'asc' ? aVal - bVal : bVal - aVal;
  });

  // Handle requirements sort
  const handleRequirementsSort = (column) => {
    if (requirementsSortColumn === column) {
      setRequirementsSortDirection(requirementsSortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setRequirementsSortColumn(column);
      setRequirementsSortDirection('asc');
    }
  };

  return (
    <div className="pending-fulfillment-vertical">

      {/* Info Blocks */}
      <div className="info-blocks-vertical">
        <div className="info-block-vertical">
          <div className="info-value-vertical">{sortedOrders.length}</div>
          <div className="info-label-vertical">Total Orders</div>
        </div>
        <div className="info-block-vertical">
          <div className="info-value-vertical">{totalShippableOrders}</div>
          <div className="info-label-vertical">Shippable Orders</div>
        </div>
        <div className="info-block-vertical">
          <div className="info-value-vertical">{fullyShippableOrders}</div>
          <div className="info-label-vertical">Fully Shippable</div>
        </div>
        <div className="info-block-vertical">
          <div className="info-value-vertical">{formatCurrency(sortedOrders.reduce((sum, order) => sum + (parseFloat(order.total) || 0), 0))}</div>
          <div className="info-label-vertical">Total Value</div>
        </div>
        <div className="info-block-vertical refresh-block-vertical" onClick={handleManualRefresh} style={{ cursor: 'pointer', flex: '0 0 auto', minWidth: '80px' }}>
          <div className={`info-value-vertical refresh-icon-vertical${refreshing ? ' spinning' : ''}`}>&#8635;</div>
          <div className="info-label-vertical">{lastUpdate ? lastUpdate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : 'Refresh'}</div>
        </div>
      </div>

      {error && (
        <div className="error-message-vertical">
          {error}
        </div>
      )}

      {/* Table Container */}
      <div className="table-container-vertical">
        <table className="fulfillment-table-vertical">
          <thead>
            <tr>
              <th onClick={() => handleColumnSort('tranId')} style={{cursor: 'pointer', width: '100px'}}>
                Order # {sortColumn === 'tranId' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>
              <th onClick={() => handleColumnSort('customerName')} style={{cursor: 'pointer'}}>
                Name {sortColumn === 'customerName' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>
              <th onClick={() => handleColumnSort('total')} style={{cursor: 'pointer', width: '90px'}}>
                Amount {sortColumn === 'total' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>
              <th onClick={() => handleColumnSort('shipDate')} style={{cursor: 'pointer', width: '70px'}}>
                Date {sortColumn === 'shipDate' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>
              <th onClick={() => handleColumnSort('age')} style={{cursor: 'pointer', width: '60px', textAlign: 'center'}}>
                Age {sortColumn === 'age' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>
              <th onClick={() => handleColumnSort('shippableLineItems')} style={{cursor: 'pointer', width: '100px', textAlign: 'center'}}>
                Shippable {sortColumn === 'shippableLineItems' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>
            </tr>
          </thead>
          <tbody>
            {currentOrders.map((order) => {
              const isFullyShippable = order.shippableLineItems === order.totalLineItems && order.totalLineItems > 0;
              const isPartiallyShippable = order.shippableLineItems > 0 && order.shippableLineItems < order.totalLineItems;
              const isNotShippable = order.shippableLineItems === 0 && order.totalLineItems > 0;

              return (
                <tr key={order.id} className={isFullyShippable ? 'fully-shippable-row' : ''}>
                  <td className="order-number-vertical">
                    {order.tranId}
                  </td>
                  <td className="customer-name-vertical">
                    {order.customerName || 'N/A'}
                  </td>
                  <td className="amount-vertical">{formatCurrency(order.total)}</td>
                  <td className="date-cell-vertical">{order.shipDate ? formatDate(order.shipDate) : 'N/A'}</td>
                  <td className={`age-cell-vertical ${order.age > 0 ? 'overdue' : order.age === 0 ? 'due-today' : ''}`}>
                    {order.age !== null ? order.age : 'N/A'}
                  </td>
                  <td className={`shippable-cell-vertical ${
                    isFullyShippable ? 'shippable-full-text' :
                    isPartiallyShippable ? 'shippable-partial-text' :
                    isNotShippable ? 'shippable-none-text' : ''
                  }`}>
                    {order.shippableLineItems || 0} / {order.totalLineItems || 0}
                    {isFullyShippable && ' ✓'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        {sortedOrders.length === 0 && !loading && (
          <div className="no-data-vertical">
            No pending fulfillment orders found
          </div>
        )}
      </div>

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <div className="pagination-vertical">
          <button
            onClick={() => goToPage(1)}
            disabled={currentPage === 1}
            className="pagination-btn-vertical"
          >
            First
          </button>
          <button
            onClick={() => goToPage(currentPage - 1)}
            disabled={currentPage === 1}
            className="pagination-btn-vertical"
          >
            Previous
          </button>
          <span className="pagination-info-vertical">
            Page {currentPage} of {totalPages} ({sortedOrders.length} total orders)
          </span>
          <button
            onClick={() => goToPage(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="pagination-btn-vertical"
          >
            Next
          </button>
          <button
            onClick={() => goToPage(totalPages)}
            disabled={currentPage === totalPages}
            className="pagination-btn-vertical"
          >
            Last
          </button>
        </div>
      )}
    </div>
  );
}

export default PendingFulfillmentVertical;
